import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/layout/Layout';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import AdminLogin from './components/AdminLogin';
import RoleGuard from './components/auth/RoleGuard';
import AdminAccessGuard from './components/auth/AdminAccessGuard';
import UsherScanGuard from './components/auth/UsherScanGuard';
import './App.css';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ConferencePage = React.lazy(() => import('./pages/ConferencePage'));
const RegistrationPage = React.lazy(() => import('./pages/RegistrationPage'));
const ExhibitorRegistrationPage = React.lazy(() => import('./pages/ExhibitorRegistrationPage'));
const SupporterRegistrationPage = React.lazy(() => import('./pages/SupporterRegistrationPage'));
const AdvertisingRegistrationPage = React.lazy(() => import('./pages/AdvertisingRegistrationPage'));
const AbstractSubmissionPage = React.lazy(() => import('./pages/AbstractSubmissionPage'));
const AbstractConfirmationPage = React.lazy(() => import('./pages/AbstractConfirmationPage'));
const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const SelcomReturnPage = React.lazy(() => import('./pages/SelcomReturnPage'));
const RegistrationConfirmationPage = React.lazy(() => import('./pages/RegistrationConfirmationPage'));
const PackagesPage = React.lazy(() => import('./pages/PackagesPage'));
const AccommodationPage = React.lazy(() => import('./pages/AccommodationPage'));
const SpeakersPage = React.lazy(() => import('./pages/SpeakersPage'));
const LeadershipPage = React.lazy(() => import('./pages/LeadershipPage'));
const TourismPage = React.lazy(() => import('./pages/TourismPage'));
const GalleryPage = React.lazy(() => import('./pages/GalleryPage'));
const PosterPage = React.lazy(() => import('./pages/PosterPage'));
const WohdPage = React.lazy(() => import('./pages/WohdPage'));
const WohdRegistrationPage = React.lazy(() => import('./pages/WohdRegistrationPage'));
const WohdPosterPage = React.lazy(() => import('./pages/WohdPosterPage'));
const WohdProgramsPage = React.lazy(() => import('./pages/WohdProgramsPage'));
const WohdSupportersPage = React.lazy(() => import('./pages/WohdSupportersPage'));
const WohdAccommodationPage = React.lazy(() => import('./pages/WohdAccommodationPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminTabRedirect = React.lazy(() => import('./components/admin/AdminTabRedirect'));
const MemberLoginPage = React.lazy(() => import('./pages/MemberLoginPage'));
const MemberRegisterPage = React.lazy(() => import('./pages/MemberRegisterPage'));
const MemberConferenceRegisterPage = React.lazy(() => import('./pages/MemberConferenceRegisterPage'));
const MemberConferencePayPage = React.lazy(() => import('./pages/MemberConferencePayPage'));
const MemberDashboardPage = React.lazy(() => import('./pages/MemberDashboardPage'));
const MemberRenewPage = React.lazy(() => import('./pages/MemberRenewPage'));
const AdminMemberDetailPage = React.lazy(() => import('./pages/AdminMemberDetailPage'));
const AdminLegacyMemberPage = React.lazy(() => import('./pages/AdminLegacyMemberPage'));
const MemberProfilePage = React.lazy(() => import('./pages/MemberProfilePage'));
const MemberEventsPage = React.lazy(() => import('./pages/MemberEventsPage'));
const MemberElectionsPage = React.lazy(() => import('./pages/MemberElectionsPage'));
const MemberDirectoryPage = React.lazy(() => import('./pages/MemberDirectoryPage'));
const UsherScanLoginPage = React.lazy(() => import('./pages/UsherScanLoginPage'));
const ConferenceScanPage = React.lazy(() => import('./pages/ConferenceScanPage'));

function App() {
  return (
    <Router>
      <React.Suspense fallback={<div className="page-loading">Loading...</div>}>
        <Routes>
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/conference" element={<Layout><ConferencePage /></Layout>} />
          <Route path="/register" element={<Layout><RegistrationPage /></Layout>} />
          <Route path="/register/exhibitor" element={<Layout><ExhibitorRegistrationPage /></Layout>} />
          <Route path="/register/supporter" element={<Layout><SupporterRegistrationPage /></Layout>} />
          <Route path="/register/advertising" element={<Layout><AdvertisingRegistrationPage /></Layout>} />
          <Route path="/abstracts" element={<Layout><AbstractSubmissionPage /></Layout>} />
          <Route path="/abstract-confirmation" element={<Layout><AbstractConfirmationPage /></Layout>} />
          <Route path="/packages" element={<Layout><PackagesPage /></Layout>} />
          <Route path="/accommodation" element={<Layout><AccommodationPage /></Layout>} />
          <Route path="/speakers" element={<Layout><SpeakersPage /></Layout>} />
          <Route path="/leadership" element={<Layout><LeadershipPage /></Layout>} />
          <Route path="/tourism" element={<Layout><TourismPage /></Layout>} />
          <Route path="/gallery" element={<Layout><GalleryPage /></Layout>} />
          <Route path="/contact" element={<Navigate to="/leadership" replace />} />
          <Route path="/poster" element={<Layout><PosterPage /></Layout>} />
          {/* WOHD (World Oral Health Day) routes */}
          <Route path="/wohd" element={<Layout><WohdPage /></Layout>} />
          <Route path="/wohd/register" element={<Layout><WohdRegistrationPage /></Layout>} />
          <Route path="/wohd/poster" element={<Layout><WohdPosterPage /></Layout>} />
          <Route path="/wohd/programs" element={<Layout><WohdProgramsPage /></Layout>} />
          <Route path="/wohd/accommodation" element={<Layout><WohdAccommodationPage /></Layout>} />
          <Route path="/wohd/supporters" element={<Layout><WohdSupportersPage /></Layout>} />
          <Route path="/payment/selcom-return" element={<Layout><SelcomReturnPage /></Layout>} />
          <Route path="/payment/:registrationId" element={<Layout><PaymentPage /></Layout>} />
          <Route path="/payment" element={<Layout><PaymentPage /></Layout>} />
          <Route path="/confirmation" element={<Layout><RegistrationConfirmationPage /></Layout>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/conference-scan/login" element={<UsherScanLoginPage />} />
          <Route
            path="/conference-scan"
            element={
              <UsherScanGuard>
                <ConferenceScanPage />
              </UsherScanGuard>
            }
          />
          <Route
            path="/member/login"
            element={<Layout><MemberLoginPage /></Layout>}
          />
          <Route
            path="/member/register"
            element={<Layout><MemberRegisterPage /></Layout>}
          />
          <Route
            path="/member/dashboard"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberDashboardPage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route
            path="/member/renew"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberRenewPage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route
            path="/member/conference/register"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberConferenceRegisterPage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route
            path="/member/conference/:registrationId/pay"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberConferencePayPage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route 
            path="/admin" 
            element={
              <ErrorBoundary>
                <ProtectedAdminRoute>
                  <AdminDashboard />
                </ProtectedAdminRoute>
              </ErrorBoundary>
            } 
          />
          <Route
            path="/member/profile"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberProfilePage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route
            path="/member/events"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberEventsPage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route
            path="/member/elections"
            element={
              <Layout>
                <RoleGuard roles={['member', 'student', 'admin', 'association_admin', 'super_admin', 'treasurer']}>
                  <MemberElectionsPage />
                </RoleGuard>
              </Layout>
            }
          />
          <Route path="/directory" element={<Layout><MemberDirectoryPage /></Layout>} />
          <Route
            path="/admin/dashboard"
            element={<AdminTabRedirect tab="home" />}
          />
          <Route
            path="/admin/members"
            element={
              <AdminTabRedirect tab="membership" membershipTab="members" preserveSearch />
            }
          />
          <Route path="/admin/payments" element={<AdminTabRedirect tab="payments" />} />
          <Route path="/admin/email-members" element={<AdminTabRedirect tab="email" />} />
          <Route path="/admin/membership-settings" element={<AdminTabRedirect tab="settings" />} />
          <Route
            path="/admin/member/legacy/:id"
            element={
              <Layout>
                <AdminAccessGuard adminAccess>
                  <AdminLegacyMemberPage />
                </AdminAccessGuard>
              </Layout>
            }
          />
          <Route
            path="/admin/member/:id"
            element={
              <Layout>
                <AdminAccessGuard adminAccess>
                  <AdminMemberDetailPage />
                </AdminAccessGuard>
              </Layout>
            }
          />
        </Routes>
      </React.Suspense>
    </Router>
  );
}

export default App;
